import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { Block } from '../classes/Block';
import { VideoSettings } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { getApiUrl } from '../utils/api';
import { useProject } from '../contexts/ProjectContext';
import LogoBlockConfig from './LogoBlockConfig';

interface HtmlPreviewProps {
  blocks: Block[];
  actualSelectedBlockIndex: number;
  videoSettings: VideoSettings;
  columnDefs: any[];
  onBlockChange: (blockIndex: number, updatedBlock: Block) => void;
}

type PreviewMode = 'single' | 'surrounding' | 'full';

interface ApiBlock {
  name: string;
  properties: {
    name: string;
    show_block_name: boolean;
    block_name_font_style: string;
    block_name_font_size: number;
    block_name_font_weight: number;
    block_name_color: string;
    column_num: number;
    distance_below_block_name: number;
    label_value_gap: number;
    margin_column: number;
    edge_distance: number;
    distance_between_rows: number;
    distance_from_previous: number;
    song_block: boolean;
    single_cell: boolean;
    credits_list: boolean;
    credits_list_separator: string;
    logo_block: boolean;
    label_font_name: string;
    label_font_size: number;
    label_font_weight: number;
    label_align: string;
    label_vertical_align: string;
    label_color: string;
    value_font_name: string;
    value_font_size: number;
    value_font_weight: number;
    value_align: string;
    value_vertical_align: string;
    value_color: string;
  };
  rows: string[][];
}

interface ApiPayload {
  video_settings: {
    background_color: string;
    fps_num: number;
    resolution: string;
    transparent: boolean;
    video_time: number;
    fonts: string | undefined;
    logos: string | undefined;
  };
  data: ApiBlock[];
}

// Map resolution string to a base numeric width used for preview calculations
const RESOLUTION_WIDTHS: Record<string, number> = {
  "HD": 1280,
  "2K": 2048,
  "UHD": 3840,
  "4K": 4096,
};

function computeVideoWidth(resolution?: string): number {
  if (!resolution) return 1920;
  return RESOLUTION_WIDTHS[resolution] ?? RESOLUTION_WIDTHS['HD'];
}

function computeVideoHeightFromAspect(width: number, aspect: string | undefined): number {
  if (!aspect || !aspect.includes(':')) return Math.round(width * (9 / 16));
  const [wStr, hStr] = aspect.split(':');
  const w = Number(wStr);
  const h = Number(hStr);
  if (!w || !h) return Math.round(width * (9 / 16));
  return Math.round(width * (h / w));
}

const HtmlPreview: React.FC<HtmlPreviewProps> = ({
  blocks,
  actualSelectedBlockIndex,
  videoSettings,
  columnDefs,
  onBlockChange,
}) => {
  const { selectedProject } = useProject();
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(0);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const savedScrollRef = useRef<number>(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const savedIframeScrollRef = useRef<number>(0);
  const iframeScrollHandlerRef = useRef<((event: Event) => void) | null>(null);
  
  // Load initial values from localStorage
  const [previewModeState, setPreviewModeState] = useState<PreviewMode>(() => {
    const saved: string | null = localStorage.getItem('fintitles_previewMode');
    if (saved === 'single' || saved === 'surrounding' || saved === 'full') {
      return saved;
    }
    return 'surrounding';
  });

  const [zoom, setZoomState] = useState<number>(() => {
    const saved: string | null = localStorage.getItem('fintitles_zoom');
    return saved !== null ? parseFloat(saved) : 1;
  });

  // Wrapper functions that update state AND localStorage
  const setPreviewMode = (value: PreviewMode): void => {
    setPreviewModeState(value);
    localStorage.setItem('fintitles_previewMode', value);
  };

  const setZoom = (value: number): void => {
    setZoomState(value);
    localStorage.setItem('fintitles_zoom', value.toString());
  };

  // Compute original content height from video settings resolution string and aspect ratio
  const originalContentHeight: number = useMemo(() => {
    const aspect: string = videoSettings?.aspect_ratio || '16:9';
    const widthPx: number = computeVideoWidth(videoSettings?.resolution);
    return computeVideoHeightFromAspect(widthPx, aspect);
  }, [videoSettings]);

  // Handler to resize iframe based on its content
  const detachIframeScrollListener = useCallback(() => {
    const iframeWindow: Window | null = iframeRef.current?.contentWindow ?? null;
    if (iframeWindow && iframeScrollHandlerRef.current) {
      iframeWindow.removeEventListener('scroll', iframeScrollHandlerRef.current);
    }
    iframeScrollHandlerRef.current = null;
  }, []);

  const attachIframeScrollListener = useCallback(() => {
    const iframeWindow: Window | null = iframeRef.current?.contentWindow ?? null;
    if (!iframeWindow) {
      return;
    }
    const handler = (event: Event) => {
      const targetDoc = (event.target as Document) ?? iframeWindow.document;
      const scrollTop: number =
        (targetDoc?.documentElement?.scrollTop ?? 0) ||
        (targetDoc?.body?.scrollTop ?? 0) ||
        iframeWindow.scrollY ||
        0;
      savedIframeScrollRef.current = scrollTop;
    };
    iframeScrollHandlerRef.current = handler;
    iframeWindow.addEventListener('scroll', handler, { passive: true });
  }, []);

  useEffect(() => {
    return () => {
      detachIframeScrollListener();
    };
  }, [detachIframeScrollListener]);

  const handleIframeLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>): void => {
    const iframe: HTMLIFrameElement = event.currentTarget;
    iframeRef.current = iframe;
    detachIframeScrollListener();
    attachIframeScrollListener();
    try {
      const iframeDoc: Document | null = iframe.contentDocument || iframe.contentWindow?.document || null;
      if (iframeDoc && iframeDoc.body) {
        // Get the actual content height
        const contentHeight: number = iframeDoc.body.scrollHeight;
        // Limit to max video height
        const finalHeight: number = Math.min(contentHeight, originalContentHeight);
        setIframeHeight(finalHeight);
        // Restore scroll position after content recalculates height
        requestAnimationFrame(() => {
          const container: HTMLDivElement | null = previewContainerRef.current;
          if (container) {
            const maxScroll: number = container.scrollHeight - container.clientHeight;
            const desiredScroll: number = Math.min(savedScrollRef.current, Math.max(0, maxScroll));
            container.scrollTo({ top: desiredScroll });
          }

          const iframeWindow: Window | null = iframeRef.current?.contentWindow ?? null;
          if (iframeWindow) {
            const docElement = iframeWindow.document?.documentElement;
            const body = iframeWindow.document?.body;
            const maxIframeScroll: number =
              Math.max(docElement?.scrollHeight ?? 0, body?.scrollHeight ?? 0) -
              (iframeRef.current?.clientHeight ?? 0);
            const desiredIframeScroll: number = Math.min(
              savedIframeScrollRef.current,
              Math.max(0, maxIframeScroll)
            );
            iframeWindow.scrollTo({ top: desiredIframeScroll });
          }
        });
      }
    } catch (e) {
      // If we can't access iframe content (CORS), use default height
      setIframeHeight(originalContentHeight);
    }
  }, [attachIframeScrollListener, detachIframeScrollListener, originalContentHeight]);

  const handleContainerScroll = useCallback((event: React.UIEvent<HTMLDivElement>): void => {
    savedScrollRef.current = event.currentTarget.scrollTop;
  }, []);

  const transformBlockToApiFormat = useCallback(
    (block: Block): ApiBlock => {
      const rows: string[][] = [];
      if (block.dataRows && block.dataRows.length > 0) {
        for (const dataRow of block.dataRows) {
          const rowValues: string[] = [];
          for (const colDef of columnDefs) {
            let value: string = String(dataRow[colDef.field] || '');
            
            // Special handling for logo blocks: ensure col2 is JSON encoded with scale key
            if (value && block.isLogoBlock && colDef.field === 'col2') {
              try {
                // If value is already a JSON string, do nothing
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _ = JSON.parse(value);
              }
              catch {
                const scaleValue: number = parseFloat(value);
                value = JSON.stringify({ scale: isNaN(scaleValue) ? 100 : scaleValue });
              }
              
            }
            rowValues.push(value);
          }
          rows.push([...rowValues]);
        }
      }
      return {
        name: block.name,
        properties: {
          name: block.name,
          show_block_name: block.shouldDisplayBlockName,
          block_name_font_style: block.blockNameFont,
          block_name_font_size: block.blockNameSize,
          block_name_font_weight: block.blockNameWeight,
          block_name_color: block.blockNameColor,
          column_num: block.numColumns,
          distance_below_block_name: block.distanceBelowBlockName,
          label_value_gap: block.labelValueGap,
          margin_column: block.marginColumn ?? 45,
          edge_distance: block.edgeDistance ?? 0,
          distance_between_rows: block.distanceBetweenRows,
          distance_from_previous: block.distanceFromPreviousBlock,
          song_block: block.isSongBlock,
          single_cell: block.isSingleCell,
          credits_list: block.isCreditsList,
          credits_list_separator: block.separator,
          logo_block: block.isLogoBlock,
          label_font_name: block.labelFontName,
          label_font_size: block.labelFontSize,
          label_font_weight: block.labelFontWeight,
          label_align: block.labelAlign,
          label_vertical_align: block.labelVerticalAlign,
          label_color: block.labelFontColor,
          value_font_name: block.valueFontName,
          value_font_size: block.valueFontSize,
          value_font_weight: block.valueFontWeight,
          value_align: block.valueAlign,
          value_vertical_align: block.valueVerticalAlign,
          value_color: block.valueFontColor,
        },
        rows: rows,
      };
    },
    [columnDefs]
  );

  const resolveBlocksForPreview = useCallback((): Block[] => {
    if (!blocks || blocks.length === 0) {
      return [];
    }

    if (actualSelectedBlockIndex < 0 || actualSelectedBlockIndex >= blocks.length) {
      return [];
    }

    if (previewModeState === 'full') {
      return blocks;
    }

    if (previewModeState === 'single') {
      return [blocks[actualSelectedBlockIndex]];
    }

    const surroundingBlocks: Block[] = [];
    if (actualSelectedBlockIndex > 0) {
      surroundingBlocks.push(blocks[actualSelectedBlockIndex - 1]);
    }
    surroundingBlocks.push(blocks[actualSelectedBlockIndex]);
    if (actualSelectedBlockIndex < blocks.length - 1) {
      surroundingBlocks.push(blocks[actualSelectedBlockIndex + 1]);
    }
    return surroundingBlocks;
  }, [blocks, actualSelectedBlockIndex, previewModeState]);

  const blocksForPreview: Block[] = useMemo(() => resolveBlocksForPreview(), [resolveBlocksForPreview]);

  useEffect(() => {
    const generateHtml = async (blocks: Block[]) => {
      if (!blocks || blocks.length === 0) {
        setHtmlContent('');
        setError(null);
        setIframeHeight(0);
        return;
      }
      setLoading(true);
      setError(null);
      setIframeHeight(0);
      try {
        const apiBlocks: ApiBlock[] = blocks.map((block) => transformBlockToApiFormat(block));

        const payload: ApiPayload = {
          video_settings: {
            background_color: videoSettings.background_color,
            fps_num: videoSettings.fps_num,
            resolution: videoSettings.resolution,
            transparent: videoSettings.transparent,
            video_time: videoSettings.video_time,
            fonts: JSON.stringify(videoSettings.fonts),
            logos: JSON.stringify(videoSettings.logos),
          },
          data: apiBlocks,
        };
        const response = await fetch(`${getApiUrl()}/html-preview?projectId=${selectedProject!.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();

        if (result.status === 'OK') {
          setHtmlContent(result.html || '');
          setError(null);
        } else {
          setError(result.message || 'Unknown error occurred');
          setHtmlContent('');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate HTML preview');
        setHtmlContent('');
      } finally {
        setLoading(false);
      }
    };

    if (blocksForPreview.length > 0 && columnDefs.length > 0) {
      generateHtml(blocksForPreview);
    } else {
      setHtmlContent('');
      setError(null);
    }
  }, [blocksForPreview, videoSettings, columnDefs, transformBlockToApiFormat, selectedProject]);

  const currentBlock: Block | undefined = useMemo(() => {
    if (!blocks || actualSelectedBlockIndex < 0 || actualSelectedBlockIndex >= blocks.length) {
      return undefined;
    }
    return blocks[actualSelectedBlockIndex];
  }, [blocks, actualSelectedBlockIndex]);

  const shouldRenderLogoConfig: boolean = Boolean(currentBlock?.isLogoBlock);

  // Derived numeric width/height used only for the preview rendering
  const previewWidth = computeVideoWidth(videoSettings?.resolution);

  const previewOptions: { value: PreviewMode; label: string }[] = useMemo(() => ([
    { value: 'single', label: t`Single Block` },
    { value: 'surrounding', label: t`Surrounding Blocks` },
    { value: 'full', label: t`Full Roller` },
  ]), []);

  return (
    <div id="html-preview" className="html-preview">
      <div
        className="preview-header"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <h3 className="preview-header-text" style={{ margin: '0 8px 12px 0' }}>
            {t`Credits Preview`}
          </h3>
          <div style={{ paddingBottom: '1vh' }} className="tooltip-wrapper">
            <span className="tooltip-icon">?</span>
            <span className="tooltip-text">
              {t`The preview doesn't show the full width of the video (even in fullscreen).`}
            </span>
          </div>
        </div>
      </div>

      {/* Controls OUTSIDE the preview container for better layout */}
      <div className="preview-controls">
        <label className="preview-mode-select" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span>{t`Preview Scope:`}</span>
          <Select
            value={previewModeState}
            onChange={(e: SelectChangeEvent<string>) => setPreviewMode(e.target.value as PreviewMode)}
            style={{ width: '200px', height: '36px', fontSize: '14px' }}
            sx={{
              '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
              '.MuiSelect-icon': { color: '#007CB2' }
            }}
            MenuProps={{ PaperProps: { style: { fontSize: 14 } } }}
          >
            {previewOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </label>

        <label className="zoom-control" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span>{t`Zoom:`}</span>
          <input
            type="range"
            min={0.05}
            max={1}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <span className="zoom-percentage">{Math.round(zoom * 100)}%</span>
          <div className="tooltip-wrapper" style={{ marginLeft: 8 }}>
            <span className="tooltip-icon">?</span>
            <span className="tooltip-text" style={{ height: "70px" }}>
              {t`This only changes the preview zoom and does not affect the actual video dimensions.`}
            </span>
          </div>
        </label>
      </div>

      {loading && (
        <div className="preview-loading">
          <p>{t`Generating preview...`}</p>
        </div>
      )}

      {error && (
        <div className="preview-error">
          <p>{t`Error: ${error}`}</p>
        </div>
      )}

      {!loading && !error && (
        <div
          className="preview-container"
          style={{
            width: '100%',
            maxHeight: '84vh',
            overflow: 'auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}
          ref={previewContainerRef}
          onScroll={handleContainerScroll}
        >
          {htmlContent ? (
            <div
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                width: `${previewWidth}px`,
                height: 'auto',
                // overflowY: 'hidden'
              }}
            >
              <iframe
                style={{
                  width: `${previewWidth}px`,
                  height: iframeHeight > 0 ? `${iframeHeight}px` : `${originalContentHeight}px`,
                  border: 'none',
                  pointerEvents: 'auto',
                  display: 'block'
                }}
                srcDoc={htmlContent}
                title="HTML Preview"
                onLoad={handleIframeLoad}
              />
            </div>
          ) : (
            <p>{t`No preview available. Please select a block with data.`}</p>
          )}
        </div>
      )}

      {/* Logo Block Configuration */}
      {shouldRenderLogoConfig && currentBlock && (
        <LogoBlockConfig
          block={currentBlock}
          blockIndex={actualSelectedBlockIndex}
          onBlockChange={(updatedBlock) => onBlockChange(actualSelectedBlockIndex, updatedBlock)}
        />
      )}
    </div>
  );
};

export default HtmlPreview;